import React, { useState, useRef, useCallback, useEffect, useId } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search } from "lucide-react";
import { useMapboxSearch, type AddressSuggestion } from "@/hooks/useMapboxSearch";

interface AddressAutocompleteProps {
  value: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  lat: string | null;
  long: string | null;
  onChange: (address: {
    formatted: string;
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    lat: string | null;
    long: string | null;
  }) => void;
  onFieldChange: (field: string, value: string) => void;
  placeholder?: string;
}

export function AddressAutocomplete({
  value,
  address_line1,
  address_line2,
  city,
  state,
  postal_code,
  country,
  onChange,
  onFieldChange,
  placeholder = "Start typing an address...",
}: AddressAutocompleteProps) {
  const { suggestions, isSearching, search, clear } = useMapboxSearch();
  const [inputValue, setInputValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [mode, setMode] = useState<"search" | "structured">(value ? "structured" : "search");

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const instanceId = useId();
  const listboxId = `address-listbox-${instanceId}`;

  // Sync external value changes
  useEffect(() => {
    setInputValue(value);
    if (value && mode === "search") {
      setMode("structured");
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const showDropdown =
    isFocused && (suggestions.length > 0 || (isSearching && inputValue.length >= 3) || (!isSearching && suggestions.length === 0 && inputValue.length >= 3));

  const handleSelect = useCallback(
    (suggestion: AddressSuggestion) => {
      setInputValue(suggestion.formatted);
      setHighlightedIndex(-1);
      setMode("structured");
      onChange({
        formatted: suggestion.formatted,
        address_line1: suggestion.address_line1,
        address_line2: "",
        city: suggestion.city,
        state: suggestion.state,
        postal_code: suggestion.postal_code,
        country: suggestion.country,
        lat: suggestion.lat,
        long: suggestion.long,
      });
      clear();
    },
    [onChange, clear]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    setHighlightedIndex(-1);
    search(val);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      e.stopPropagation();
      handleSelect(suggestions[highlightedIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      clear();
      setHighlightedIndex(-1);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Don't blur if clicking within the container (dropdown)
    if (containerRef.current?.contains(e.relatedTarget as Node)) {
      return;
    }

    setIsFocused(false);
    setHighlightedIndex(-1);

    // If text was typed but no suggestion was selected, emit raw text and switch to structured
    if (inputValue && mode === "search") {
      onChange({
        formatted: inputValue,
        address_line1: inputValue,
        address_line2: "",
        city: "",
        state: "",
        postal_code: "",
        country: "",
        lat: null,
        long: null,
      });
      setMode("structured");
    }

    clear();
  };

  const handleSearchAgain = () => {
    setMode("search");
    setInputValue("");
    onChange({
      formatted: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      postal_code: "",
      country: "",
      lat: null,
      long: null,
    });
    // Focus the search input after switching
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleStructuredFieldChange = (field: string, fieldValue: string) => {
    onFieldChange(field, fieldValue);
    // Rebuild formatted string from parts
    const parts = {
      address_line1,
      address_line2,
      city,
      state,
      postal_code,
      country,
      [field]: fieldValue,
    };
    const line1 = parts.address_line1;
    const line2 = parts.address_line2;
    const cityStateZip = [parts.city, parts.state, parts.postal_code].filter(Boolean).join(", ");
    const formatted = [line1, line2, cityStateZip, parts.country].filter(Boolean).join(", ");
    onFieldChange("formatted", formatted);
  };

  // --- Search mode ---
  if (mode === "search") {
    return (
      <div ref={containerRef} className="relative">
        <div className="relative">
          <Input
            ref={inputRef}
            className="h-8 text-sm pr-8"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoComplete="off"
            role="combobox"
            aria-expanded={showDropdown}
            aria-controls={listboxId}
            aria-activedescendant={
              highlightedIndex >= 0
                ? `${listboxId}-option-${highlightedIndex}`
                : undefined
            }
            aria-autocomplete="list"
          />
          {isSearching && (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>

        {showDropdown && (
          <div
            id={listboxId}
            role="listbox"
            className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-[200px] overflow-y-auto"
          >
            {suggestions.length > 0 ? (
              suggestions.map((suggestion, index) => (
                <div
                  key={suggestion.formatted}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  className={`px-3 py-2 text-sm cursor-pointer ${
                    index === highlightedIndex ? "bg-accent" : "hover:bg-accent"
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelect(suggestion);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {suggestion.formatted}
                </div>
              ))
            ) : isSearching ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                Searching...
              </div>
            ) : (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                No addresses found
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // --- Structured fields mode ---
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto py-0 px-1 text-[10px] text-muted-foreground hover:text-foreground"
          onClick={handleSearchAgain}
        >
          <Search className="h-3 w-3 mr-0.5" />
          Search again
        </Button>
      </div>

      <div>
        <label className="text-[10px] text-muted-foreground">Address Line 1</label>
        <Input
          className="h-7 text-xs"
          value={address_line1}
          onChange={(e) => handleStructuredFieldChange("address_line1", e.target.value)}
          placeholder="Street address"
        />
      </div>

      <div>
        <label className="text-[10px] text-muted-foreground">Apt/Suite (optional)</label>
        <Input
          className="h-7 text-xs"
          value={address_line2}
          onChange={(e) => handleStructuredFieldChange("address_line2", e.target.value)}
          placeholder="Apt, Suite, Unit, etc."
        />
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] text-muted-foreground">City</label>
          <Input
            className="h-7 text-xs"
            value={city}
            onChange={(e) => handleStructuredFieldChange("city", e.target.value)}
            placeholder="City"
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">State</label>
          <Input
            className="h-7 text-xs"
            value={state}
            onChange={(e) => handleStructuredFieldChange("state", e.target.value)}
            placeholder="State"
          />
        </div>
        <div>
          <label className="text-[10px] text-muted-foreground">Postal Code</label>
          <Input
            className="h-7 text-xs"
            value={postal_code}
            onChange={(e) => handleStructuredFieldChange("postal_code", e.target.value)}
            placeholder="Zip"
          />
        </div>
      </div>

      <div>
        <label className="text-[10px] text-muted-foreground">Country</label>
        <Input
          className="h-7 text-xs"
          value={country}
          onChange={(e) => handleStructuredFieldChange("country", e.target.value)}
          placeholder="Country"
        />
      </div>
    </div>
  );
}
